import type { AxiosRequestConfig } from "axios";
import type { ReactNode } from "react";

// ------------------------------------ TYPES ------------------------------------

/** Совпадает с `DownloadFileMeta` из `@synthesis/shared-utils` (второй аргумент `useDownload()`). */
type DownloadHookFileMeta = {
  id: string;
  baseName: string;
  ext?: string;
};

/** Метаданные уже загруженного файла на сервере. */
export type UploadedFileMeta = {
  id?: string;
  name?: string;
  baseName?: string;
  ext?: string;
  type?: string;
};

/** Возвращает Blob загруженного файла по метаданным с бэкенда. */
export type FetchLoadedFile = (fileMeta: UploadedFileMeta) => Promise<Blob>;

/** Тело запроса загрузки одного файла. */
export type UploaderRequestBody = {
  sourceId?: string;
  sourceType?: string;
  parentId?: string;
  document: File;
};

/** Ответ сервиса загрузки: метаданные файла лежат в `data` (опционально под `fileMeta`). */
export type UploaderRequestResponse = {
  data: { fileMeta?: UploadedFileMeta } & Record<string, unknown>;
};

/** Хендл запроса: позволяет дождаться ответа и отменить запрос. */
export type UploaderRequestHandle = {
  abort: () => void;
  fetch: () => Promise<UploaderRequestResponse>;
};

/** Сервис загрузки (например `FilesystemService.postFile`). */
export type UploaderRequest = (body: UploaderRequestBody, config: AxiosRequestConfig) => UploaderRequestHandle;

export type UploaderState = {
  uploadProgress: number;
  currentRequest: null | { abort: () => void };
  indexOfUploadingFile: { current: number; total: number };
  uploadedFiles: UploaderListItem[];
  isUploading: boolean;
  errors: string[];
};

export type UploaderCompleteCallback = (uploadedFiles: UploaderListItem[], errors: string[]) => void;

export type UploaderAction =
  | { type: "COMPLETE"; payload: { cb: UploaderCompleteCallback } }
  | { type: "ABORT_REQUEST" }
  | { type: "UPLOAD_PROGRESS_UPDATE"; payload: number }
  | { type: "CURRENT_REQUEST_UPDATE"; payload: UploaderState["currentRequest"] }
  | { type: "indexOfUploadingFileUpdate"; payload: UploaderState["indexOfUploadingFile"] }
  | { type: "UPLOADED_FILES_UPDATE"; payload: UploaderListItem }
  | { type: "UPLOADED_FILES_DELETE_ONE"; payload: UploaderListItem }
  | { type: "IS_UPLOADING_UPDATE"; payload: boolean }
  | { type: "ERRORS_UPDATE"; payload: string }
  | { type: "ERRORS_RESET" }
  | { type: "ERRORS_FILTER"; payload: string }
  | { type: "UPLOADED_FILES_RESET" };

export type UploaderProps = {
  /** Сервис загрузки (например FilesystemService.postFile). */
  request?: UploaderRequest;
  sourceId?: string;
  sourceType?: string;
  parentId?: string;
  formats?: string[];
  multiple?: boolean;
  axiosConfig?: AxiosRequestConfig;
  onSelectFiles?: (files: File[]) => void;
  onDeleteFromList?: (fileMeta: UploaderListItem) => void;
  onSuccess?: (data: unknown) => unknown;
  onError?: (fileName: string, error: Error) => unknown;
  onComplete?: UploaderCompleteCallback;
  defaultLoadedFiles?: UploaderListItem[];
  handleAttachFiles?: (files: File[]) => unknown;
  description?: string;
  allowDeleteFromList?: boolean;
  onDownload?: (fileMeta: UploaderListItem) => Promise<void>;
  isViewFileList?: boolean;
  resetBeforeUpload?: boolean;
  label?: string;
  required?: boolean;
  className?: string;
  fetchLoadedFile?: FetchLoadedFile;
};

export type FileListProps = {
  files: UploaderListItem[];
  onDeleteFile?: (fileMeta: UploaderListItem) => void;
  onDownloadFile?: (fileMeta: UploaderListItem) => Promise<void>;
  fetchLoadedFile?: FetchLoadedFile;
};

export type LineProps = {
  uploadProgress: number;
};

/** Элемент из react-dnd для нативного дропа файлов. */
export type NativeFileDropItem = {
  files: File[];
};

export type TargetBoxProps = {
  onDrop: (item: NativeFileDropItem) => void;
  isUploading: boolean;
  onAbort: () => void;
  uploadProgress: number;
  indexOfUploadingFile: { current: number; total: number };
  multiple?: boolean;
  formats?: string[];
  description?: string;
};

export type FileRowDropdownOptionItem = {
  type?: "download" | "delete";
  label: ReactNode;
  callback: () => void;
};

export type DemoUploaderContainerProps = TargetBoxProps & {
  hasError?: boolean;
  uploadedFiles?: UploaderListItem[];
  onCloseErrorNotice?: () => void;
};

export type ErrorsListProps = {
  errors: string[];
};

export type UploaderListItem = File | UploadedFileMeta;

// ------------------------------------ UTILS ------------------------------------

export const formatString = (formats: string[] | undefined): string => {
  if (!formats?.length) {
    return "";
  }
  return formats.reduce((acc: string, item: string, index) => {
    if (formats.length === 1) {
      return item;
    }
    if (index === formats.length - 1 && formats.length > 1) {
      acc += ` ${item}`;
    } else if (index === formats.length - 2) {
      acc += ` ${item}, `;
    } else {
      acc += ` ${item},`;
    }
    return acc;
  }, "");
};

/** Сведения для `useDownload`: обязательны `id` и `baseName`. */
export const uploadedMetaToDownloadMeta = (meta: UploadedFileMeta): DownloadHookFileMeta | null => {
  if (!meta.id) {
    return null;
  }

  const baseName = meta.baseName ?? (meta.name ? meta.name.replace(/\.[^.]+$/, "") : undefined);

  if (!baseName) {
    return null;
  }

  const ext = meta.ext ?? (meta.name?.includes(".") ? meta.name.split(".").pop() : undefined);

  return { id: meta.id, baseName, ...(ext ? { ext } : {}) };
};

export const reducer = (state: UploaderState, action: UploaderAction): UploaderState => {
  if (action.type === "COMPLETE") {
    action.payload.cb(state.uploadedFiles, state.errors);
    return state;
  }
  if (action.type === "ABORT_REQUEST") {
    state.currentRequest?.abort();
    return state;
  }
  if (action.type === "UPLOAD_PROGRESS_UPDATE") {
    return { ...state, uploadProgress: action.payload };
  }
  if (action.type === "CURRENT_REQUEST_UPDATE") {
    return { ...state, currentRequest: action.payload };
  }
  if (action.type === "indexOfUploadingFileUpdate") {
    return { ...state, indexOfUploadingFile: action.payload };
  }
  if (action.type === "UPLOADED_FILES_UPDATE") {
    return { ...state, uploadedFiles: [action.payload, ...state.uploadedFiles] };
  }
  if (action.type === "UPLOADED_FILES_DELETE_ONE") {
    return { ...state, uploadedFiles: state.uploadedFiles.filter((item) => item !== action.payload) };
  }
  if (action.type === "IS_UPLOADING_UPDATE") {
    return { ...state, isUploading: action.payload };
  }
  if (action.type === "ERRORS_UPDATE") {
    return { ...state, errors: [action.payload, ...state.errors] };
  }
  if (action.type === "ERRORS_RESET") {
    return { ...state, errors: [] };
  }
  if (action.type === "ERRORS_FILTER") {
    return { ...state, errors: state.errors.filter((item) => item !== action.payload) };
  }
  if (action.type === "UPLOADED_FILES_RESET") {
    return { ...state, uploadedFiles: [] };
  }

  const unreachable = action as { type: string };
  throw new Error(`Reducer: Type "${unreachable.type}" was not found`);
};
